/**
 * SharePoint Jobs List Page
 * Displays all configured SharePoint image metadata extraction jobs
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, PlayCircle, PauseCircle, Calendar, BarChart3, Play } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  fetchSharePointJobs,
  deleteSharePointJob,
  updateSharePointJob,
  triggerSharePointJob,
  type SharePointJob,
} from "@/lib/sharepoint-api";
import { QualityStatsModal } from "@/components/sharepoint/quality-stats-modal";
import { SharePointLayout } from "./layout";

export default function SharePointJobsPage() {
  const [jobs, setJobs] = useState<SharePointJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [qualityStatsJob, setQualityStatsJob] = useState<{ id: string; name: string } | null>(null);
  const { toast } = useToast();

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await fetchSharePointJobs();
      setJobs(data);
    } catch (error: any) {
      toast({
        title: "Error loading jobs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const handleDelete = async () => {
    if (!deleteJobId) return;

    try {
      await deleteSharePointJob(deleteJobId);
      toast({
        title: "Job deleted",
        description: "SharePoint job has been deleted successfully",
      });
      setDeleteJobId(null);
      loadJobs();
    } catch (error: any) {
      toast({
        title: "Error deleting job",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleToggleEnabled = async (job: SharePointJob) => {
    try {
      await updateSharePointJob(job.id, {
        ...job,
        isEnabled: !job.isEnabled,
      });
      toast({
        title: job.isEnabled ? "Job disabled" : "Job enabled",
        description: `SharePoint job has been ${job.isEnabled ? "disabled" : "enabled"}`,
      });
      loadJobs();
    } catch (error: any) {
      toast({
        title: "Error updating job",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleRunNow = async (job: SharePointJob) => {
    try {
      const result = await triggerSharePointJob(job.id);
      toast({
        title: "Job triggered",
        description: `${job.name} has been queued for immediate execution`,
      });
    } catch (error: any) {
      toast({
        title: "Error triggering job",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <SharePointLayout>
      <div className="p-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>SharePoint Image Jobs</CardTitle>
                <CardDescription>
                  Manage SharePoint image metadata extraction jobs
                </CardDescription>
              </div>
              <Link href="/sharepoint/jobs/new">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  New Job
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No SharePoint jobs configured yet.</p>
              <Link href="/sharepoint/jobs/new">
                <Button variant="outline" className="mt-4">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Job
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sites</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Last Modified</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{job.name}</div>
                        {job.description && (
                          <div className="text-sm text-muted-foreground">
                            {job.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={job.isEnabled ? "default" : "secondary"}>
                          {job.isEnabled ? "Enabled" : "Disabled"}
                        </Badge>
                        {job.dryRun && (
                          <Badge variant="outline">Dry run</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {job.sites.length} site{job.sites.length !== 1 ? "s" : ""}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3" />
                        {job.schedule || <span className="text-muted-foreground">None</span>}
                      </div>
                      {job.scheduleDescription && (
                        <div className="text-xs text-muted-foreground">
                          {job.scheduleDescription}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {formatDate(job.modifiedOn)}
                      </div>
                      {job.modifiedBy && (
                        <div className="text-xs text-muted-foreground">
                          by {job.modifiedBy}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRunNow(job)}
                          title="Run now"
                        >
                          <Play className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setQualityStatsJob({ id: job.id, name: job.name })}
                          title="View quality statistics"
                        >
                          <BarChart3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleEnabled(job)}
                          title={job.isEnabled ? "Disable job" : "Enable job"}
                        >
                          {job.isEnabled ? (
                            <PauseCircle className="h-4 w-4" />
                          ) : (
                            <PlayCircle className="h-4 w-4" />
                          )}
                        </Button>
                        <Link href={`/sharepoint/jobs/edit/${job.id}`}>
                          <Button variant="ghost" size="sm" title="Edit job">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteJobId(job.id)}
                          title="Delete job"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          </CardContent>
        </Card>

      <AlertDialog open={!!deleteJobId} onOpenChange={() => setDeleteJobId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the SharePoint job and its associated scheduled task.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {qualityStatsJob && (
        <QualityStatsModal
          jobId={qualityStatsJob.id}
          jobName={qualityStatsJob.name}
          open={!!qualityStatsJob}
          onOpenChange={(open) => !open && setQualityStatsJob(null)}
        />
      )}
      </div>
    </SharePointLayout>
  );
}
